import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ContactMail from '@mui/icons-material/ContactMail';
import { withTranslation } from 'react-i18next';
import ContactModeListComponent from './ContactModeListComponent';
import { bindActionCreators, compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { loadContactModes, saveContactModes } from '../actionCreators';
import { loadContactModeTypes } from 'actions/actionCreators/dictionariesActions';
import { getOfferPartnerContactModes } from '../selectors';
import { emptyArray } from 'constants/emptyConst';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import * as V from '@totalsoft/pure-validations';
import { withParams } from 'providers/ParamsConsumer';
import { isMobilePhoneByCountry } from 'utils/pure-validation/primitiveValidators';
import { consts } from 'constants/contactModeTypes';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';

const getContactModesValidator = params =>
  V.items(
    V.fromModel(_item =>
      V.shape({
        contactModeId: V.required,
        contactInfo:
          [
            V.required,
            V.email |> V.when(_item.contactModeId === consts.Email),
            isMobilePhoneByCountry(params.countryCode) |> V.when(_item.contactModeId === consts.Mobile)
          ] |> V.stopOnFirstFailure
      })
    )
  );

export const OfferPartnerContactModesContainer = ({
  t,
  partnerId,
  offerHeader,
  actions,
  contactModes,
  contactModeTypes,
  loaded,
  params,
  saving
}) => {
  const dispatch = useDispatch();
  const { documentId, siteId } = offerHeader;
  const [cardExpanded, setExpanded] = useState(false);
  const [localContactModes, setLocalContactModes] = useState(emptyArray);
  const [validDefault, setValidDefault] = useState({ email: true, mobile: true, phone: true });

  const [isDirty, setIsDirty] = useState(false);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const validator = useMemo(() => getContactModesValidator(params), [params]);
  const [contactModesValidation, validateContactModes, resetDirtyInfo] = useDirtyFieldValidation(validator, dirtyInfo);

  const onExpand = useCallback((_, expanded) => {
    setExpanded(!expanded);
  }, []);

  const validateUniqueDefaultEmail = useCallback(() => {
    return localContactModes.filter(contactMode => contactMode.isDefault === true && contactMode.contactModeId === consts.Email);
  }, [localContactModes]);

  const validateUniqueDefaultMobile = useCallback(() => {
    return localContactModes.filter(contactMode => contactMode.isDefault === true && contactMode.contactModeId === consts.Mobile);
  }, [localContactModes]);

  const validateUniqueDefaultPhone = useCallback(() => {
    return localContactModes.filter(contactMode => contactMode.isDefault === true && contactMode.contactModeId === consts.Phone);
  }, [localContactModes]);

  const loadLocalContactModes = useCallback(async () => {
    return await actions.loadContactModes(offerHeader.documentId, offerHeader.siteId, partnerId);
  }, [actions, offerHeader, partnerId]);

  const addContactMode = useCallback(() => {
    let contactModeList = [...localContactModes, {}];
    if (localContactModes.length === 0) contactModeList = [...localContactModes, { isDefault: true }];

    setLocalContactModes(contactModeList);
    setIsDirty(true);
    setValidDefault(prev => ({ ...prev, email: true, phone: true, mobile: true }));
  }, [localContactModes]);

  const deleteContactMode = useCallback(
    item => {
      setDirtyInfo(`${localContactModes.indexOf(item)}`);
      let newList = localContactModes.filter(l => localContactModes.indexOf(l) !== localContactModes.indexOf(item));
      setLocalContactModes(newList);
      setIsDirty(true);
    },
    [localContactModes, setDirtyInfo]
  );

  const handlePropertyChange = useCallback(
    idx => prop => value => {
      setDirtyInfo(`${idx}.${prop}`);
      let newDefault = localContactModes[idx].isDefault;
      if (prop === 'contactModeId' && !localContactModes.find(x => x.contactModeId === value && x.isDefault)) {
        newDefault = true;
      }
      let newList = Object.assign([], {
        ...localContactModes,
        [idx]: {
          ...localContactModes[idx],
          isDefault: newDefault,
          [prop]: value
        }
      });

      setLocalContactModes(newList);
      setIsDirty(true);
      setValidDefault(prev => ({ ...prev, email: true, phone: true, mobile: true }));
    },
    [localContactModes, setDirtyInfo]
  );

  const saveContactModeList = useCallback(() => {
    if (!validateContactModes(localContactModes)) return;

    const counterDefaultEmail = validateUniqueDefaultEmail();
    const counterDefaultMobilePhone = validateUniqueDefaultMobile();
    const counterDefaultPhone = validateUniqueDefaultPhone();

    if (counterDefaultEmail.length > 1) {
      setValidDefault(prev => ({ ...prev, email: false }));
    } else if (counterDefaultMobilePhone.length > 1) {
      setValidDefault(prev => ({ ...prev, mobile: false }));
    } else if (counterDefaultPhone.length > 1) {
      setValidDefault(prev => ({ ...prev, phone: false }));
    } else {
      dispatch(
        saveContactModes(documentId, siteId, partnerId, localContactModes, () => {
          setIsDirty(false);
          resetDirtyInfo();
        })
      );
    }
  }, [
    dispatch,
    documentId,
    siteId,
    validateContactModes,
    partnerId,
    validateUniqueDefaultEmail,
    validateUniqueDefaultMobile,
    validateUniqueDefaultPhone,
    localContactModes,
    resetDirtyInfo
  ]);

  useEffect(() => {
    if (cardExpanded && !loaded) loadLocalContactModes();
  }, [loaded, cardExpanded, offerHeader, loadLocalContactModes, localContactModes]);

  useEffect(() => {
    if (loaded && !isDirty) {
      setLocalContactModes(contactModes);
    }
  }, [loaded, contactModes, isDirty]);

  useEffect(() => {
    if (cardExpanded) actions.loadContactModeTypes();
  }, [cardExpanded, actions]);

  useEffect(() => {
    validateContactModes(localContactModes, dirtyInfo);
  }, [localContactModes, dirtyInfo, validateContactModes]);

  return (
    <CollapseOrBlankCard
      id='contactModesCard'
      icon={ContactMail}
      title={t('OfferEdit.Client.ContactMode.PhoneAndEmail')}
      expanded={cardExpanded}
      onToggle={onExpand}
      canExpand={partnerId != null}
      content={
        <ContactModeListComponent
          contactModes={localContactModes}
          contactModeTypes={contactModeTypes}
          addContactMode={addContactMode}
          deleteContactMode={deleteContactMode}
          saveContactModeList={saveContactModeList}
          onPropertyChange={handlePropertyChange}
          validation={contactModesValidation}
          validDefault={validDefault}
          isDirty={isDirty}
          saving={saving}
        />
      }
    />
  );
};

OfferPartnerContactModesContainer.propTypes = {
  partnerId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  offerHeader: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  contactModes: PropTypes.array,
  contactModeTypes: PropTypes.array.isRequired,
  loaded: PropTypes.bool.isRequired,
  params: PropTypes.object,
  saving: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  const { partnerId } = ownProps;
  const { loaded, content } = getOfferPartnerContactModes(state, partnerId);

  return {
    loaded,
    contactModes: content,
    offerHeader: state.currentOffer.header,
    contactModeTypes: state.dictionaries.contactModeTypes.isLoaded ? state.dictionaries.contactModeTypes.items : [],
    saving: state.currentOffer.clientContactModesData[partnerId]?.saving
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadContactModes,
        loadContactModeTypes,
        saveContactModes
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(withParams(OfferPartnerContactModesContainer));
